import { buildAvailabilityMessageForProduct } from './availabilityMessage';
import { absoluteUrl } from './pageSeo';
import { getProductPath } from './productUrl';
import { resolveStoreChatUrlForProduct } from './storeChatLookup';

const DEFAULT_WHATSAPP = '254708177021';

let cachedMessageTemplate = null;
let cachedStoreOutlets = [];

export function setAvailabilityMessageTemplate(template) {
  cachedMessageTemplate = template || null;
}

/** Outlets from GET /api/outlets — used to resolve store WhatsApp when product has no vendorChatUrl. */
export function setStoreOutletsForChatLookup(outlets) {
  cachedStoreOutlets = Array.isArray(outlets) ? outlets : [];
}

function getPlatformWhatsAppDigits() {
  const raw = (process.env.REACT_APP_WHATSAPP_NUMBER || DEFAULT_WHATSAPP).replace(/\D/g, '');
  return raw || DEFAULT_WHATSAPP;
}

/**
 * Resolve vendor chat link for “Check availability” (per store listing).
 */
export function getVendorChatUrl(product) {
  const fromStore = resolveStoreChatUrlForProduct(product, cachedStoreOutlets);
  if (fromStore) return fromStore;

  const direct =
    product?.vendorChatUrl ||
    product?.vendor?.chatUrl ||
    product?.vendor?.whatsappUrl;
  if (direct && String(direct).trim()) {
    const url = String(direct).trim();
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  }

  const phone =
    product?.vendor?.contact?.phone ||
    product?.vendor?.contact?.whatsapp ||
    product?.vendor?.phone;
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length >= 9) {
    let d = digits;
    if (d.length === 10 && d.startsWith('0')) {
      d = `254${d.slice(1)}`;
    }
    return `https://wa.me/${d}`;
  }

  return null;
}

export function buildAvailabilityCheckMessage(product, options = {}) {
  const template = options.template ?? cachedMessageTemplate;
  return buildAvailabilityMessageForProduct(product, {
    template,
    productPageUrl:
      options.productPageUrl ||
      (product ? absoluteUrl(getProductPath(product)) : ''),
  });
}

/**
 * Append pre-filled message when the chat provider supports it.
 */
export function withPrefilledChatMessage(chatUrl, message) {
  const base = String(chatUrl || '').trim();
  const text = String(message || '').trim();
  if (!base) return null;
  if (!text) return base;

  try {
    const normalized = /^https?:\/\//i.test(base) ? base : `https://${base}`;
    const url = new URL(normalized);

    if (/wa\.me\/message\//i.test(url.href)) {
      return url.href;
    }

    const textKeys = ['text', 'message', 'body', 'prefilled_message', 'msg'];
    for (const key of textKeys) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, text);
        return url.href;
      }
    }

    if (/wa\.me$/i.test(url.hostname) || /whatsapp\.com$/i.test(url.hostname)) {
      url.searchParams.set('text', text);
      return url.href;
    }

    url.searchParams.set('text', text);
    return url.href;
  } catch {
    const digits = base.replace(/\D/g, '');
    if (digits.length >= 9) {
      return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    }
    return base;
  }
}

export function buildAvailabilityCheckUrl(product, options = {}) {
  const message = buildAvailabilityCheckMessage(product, options);
  const vendorChat = getVendorChatUrl(product);
  if (vendorChat) {
    return withPrefilledChatMessage(vendorChat, message);
  }
  return `https://wa.me/${getPlatformWhatsAppDigits()}?text=${encodeURIComponent(message)}`;
}

export function openVendorAvailabilityCheck(product, options = {}) {
  const url = buildAvailabilityCheckUrl(product, options);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
